import type { QueryClient } from '@tanstack/react-query';
import {
  createRootRouteWithContext,
  createRoute,
  createRouter,
  Outlet,
  redirect,
} from '@tanstack/react-router';
import { RouteErrorFallback, RouteNotFound } from './components/route-fallbacks.js';
import { resolveStaffSession } from './hooks/use-staff-session.js';
import { queryClient } from './query-client.js';
import { AuditPage, validateAuditSearch } from './routes/audit-page.js';
import { AuthenticatedLayout } from './routes/authenticated-layout.js';
import { CompaniesListPage } from './routes/companies-list-page.js';
import { CompanyDetailPage } from './routes/company-detail-page.js';
import { DashboardPage } from './routes/dashboard-page.js';
import { DownloadsPage } from './routes/downloads-page.js';
import { FaqPage } from './routes/faq-page.js';
import { LegalPage } from './routes/legal-page.js';
import { LoginRoute } from './routes/login-route.js';
import { MetricsDashboardPage } from './routes/metrics-dashboard-page.js';
import { ModerationConfigPage } from './routes/moderation-config-page.js';
import { NotificationsPage } from './routes/notifications-page.js';
import { PackDetailPage } from './routes/pack-detail-page.js';
import { PackSubmissionDetailPage } from './routes/pack-submission-detail-page.js';
import { PackSubmissionsListPage } from './routes/pack-submissions-list-page.js';
import { PacksCatalogListPage } from './routes/packs-catalog-list-page.js';
import { PlanCreatePage } from './routes/plan-create-page.js';
import { PlansPage } from './routes/plans-page.js';
import { PlatformSettingsPage } from './routes/platform-settings-page.js';
import { PublisherDetailPage } from './routes/publisher-detail-page.js';
import { PublisherGuidePage } from './routes/publisher-guide-page.js';
import { PublishersListPage } from './routes/publishers-list-page.js';
import { ReleaseRegisterPage } from './routes/release-register-page.js';
import { ReleasesPage } from './routes/releases-page.js';
import { StaffManagementPage } from './routes/staff-page.js';
import { SupportChatPage } from './routes/support-chat-page.js';
import { SupportInboxPage } from './routes/support-inbox-page.js';
import { TargetedPlanCreatePage } from './routes/targeted-plan-create-page.js';
import { UserDetailPage } from './routes/user-detail-page.js';
import { UsersListPage } from './routes/users-list-page.js';

/* Árvore de rotas code-based (ADR-0354), igual ao desktop — sem file-based codegen
   (`@tanstack/router-plugin` ausente do catalog). Guard de auth no `beforeLoad` da
   layout-route pathless `authenticated`. */
interface RouterContext {
  readonly queryClient: QueryClient;
}

const rootRoute = createRootRouteWithContext<RouterContext>()({ component: Outlet });

const indexRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/',
  beforeLoad: () => {
    throw redirect({ to: '/dashboard' });
  },
});

const loginRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/login',
  component: LoginRoute,
});

const authenticatedRoute = createRoute({
  getParentRoute: () => rootRoute,
  id: 'authenticated',
  beforeLoad: async ({ context }) => {
    /* Sessão via `ensureQueryData` (cache) — não bloqueia a navegação num GET /me por clique. */
    const authenticated = await resolveStaffSession(context.queryClient);
    if (!authenticated) {
      throw redirect({ to: '/login' });
    }
  },
  component: AuthenticatedLayout,
});

const dashboardRoute = createRoute({
  getParentRoute: () => authenticatedRoute,
  path: '/dashboard',
  component: DashboardPage,
});

const companiesRoute = createRoute({
  getParentRoute: () => authenticatedRoute,
  path: '/companies',
  component: CompaniesListPage,
});

const companyDetailRoute = createRoute({
  getParentRoute: () => authenticatedRoute,
  path: '/companies/$id',
  component: CompanyDetailPage,
});

const usersRoute = createRoute({
  getParentRoute: () => authenticatedRoute,
  path: '/users',
  component: UsersListPage,
});

const userDetailRoute = createRoute({
  getParentRoute: () => authenticatedRoute,
  path: '/users/$id',
  component: UserDetailPage,
});

const plansRoute = createRoute({
  getParentRoute: () => authenticatedRoute,
  path: '/plans',
  component: PlansPage,
});

const planCreateRoute = createRoute({
  getParentRoute: () => authenticatedRoute,
  path: '/plans/new',
  component: PlanCreatePage,
});

const targetedPlanCreateRoute = createRoute({
  getParentRoute: () => authenticatedRoute,
  path: '/plans/new-targeted',
  component: TargetedPlanCreatePage,
});

const supportRoute = createRoute({
  getParentRoute: () => authenticatedRoute,
  path: '/support',
  component: SupportInboxPage,
});

const supportChatRoute = createRoute({
  getParentRoute: () => authenticatedRoute,
  path: '/support/$ticketId',
  component: SupportChatPage,
});

const metricsRoute = createRoute({
  getParentRoute: () => authenticatedRoute,
  path: '/metrics',
  component: MetricsDashboardPage,
});

const downloadsRoute = createRoute({
  getParentRoute: () => authenticatedRoute,
  path: '/downloads',
  component: DownloadsPage,
});

const packSubmissionsRoute = createRoute({
  getParentRoute: () => authenticatedRoute,
  path: '/pack-submissions',
  component: PackSubmissionsListPage,
});

const packSubmissionDetailRoute = createRoute({
  getParentRoute: () => authenticatedRoute,
  path: '/pack-submissions/$id',
  component: PackSubmissionDetailPage,
});

const packsCatalogRoute = createRoute({
  getParentRoute: () => authenticatedRoute,
  path: '/packs',
  component: PacksCatalogListPage,
});

const packDetailRoute = createRoute({
  getParentRoute: () => authenticatedRoute,
  path: '/packs/$packId',
  component: PackDetailPage,
});

const publishersRoute = createRoute({
  getParentRoute: () => authenticatedRoute,
  path: '/publishers',
  component: PublishersListPage,
});

const publisherDetailRoute = createRoute({
  getParentRoute: () => authenticatedRoute,
  path: '/publishers/$id',
  component: PublisherDetailPage,
});

const moderationConfigRoute = createRoute({
  getParentRoute: () => authenticatedRoute,
  path: '/moderation-config',
  component: ModerationConfigPage,
});

const faqRoute = createRoute({
  getParentRoute: () => authenticatedRoute,
  path: '/faq',
  component: FaqPage,
});

const publisherGuideRoute = createRoute({
  getParentRoute: () => authenticatedRoute,
  path: '/publisher-guide',
  component: PublisherGuidePage,
});

const legalRoute = createRoute({
  getParentRoute: () => authenticatedRoute,
  path: '/legal',
  component: LegalPage,
});

const notificationsRoute = createRoute({
  getParentRoute: () => authenticatedRoute,
  path: '/notifications',
  component: NotificationsPage,
});

const releasesRoute = createRoute({
  getParentRoute: () => authenticatedRoute,
  path: '/releases',
  component: ReleasesPage,
});

const releaseRegisterRoute = createRoute({
  getParentRoute: () => authenticatedRoute,
  path: '/releases/register',
  component: ReleaseRegisterPage,
});

const staffRoute = createRoute({
  getParentRoute: () => authenticatedRoute,
  path: '/staff',
  component: StaffManagementPage,
});

const settingsRoute = createRoute({
  getParentRoute: () => authenticatedRoute,
  path: '/settings',
  component: PlatformSettingsPage,
});

const auditRoute = createRoute({
  getParentRoute: () => authenticatedRoute,
  path: '/audit',
  /* Deep-link do detalhe de user/empresa: `?targetType=&targetId=` escopa a trilha (ADR-0713). */
  validateSearch: validateAuditSearch,
  component: AuditPage,
});

const routeTree = rootRoute.addChildren([
  indexRoute,
  loginRoute,
  authenticatedRoute.addChildren([
    dashboardRoute,
    companiesRoute,
    companyDetailRoute,
    usersRoute,
    userDetailRoute,
    plansRoute,
    planCreateRoute,
    targetedPlanCreateRoute,
    supportRoute,
    supportChatRoute,
    metricsRoute,
    downloadsRoute,
    packSubmissionsRoute,
    packSubmissionDetailRoute,
    packsCatalogRoute,
    packDetailRoute,
    publishersRoute,
    publisherDetailRoute,
    moderationConfigRoute,
    faqRoute,
    publisherGuideRoute,
    legalRoute,
    notificationsRoute,
    releasesRoute,
    releaseRegisterRoute,
    staffRoute,
    settingsRoute,
    auditRoute,
  ]),
]);

export const router = createRouter({
  routeTree,
  context: { queryClient },
  defaultErrorComponent: RouteErrorFallback,
  defaultNotFoundComponent: RouteNotFound,
});

declare module '@tanstack/react-router' {
  interface Register {
    router: typeof router;
  }
}
